import { useMutation } from '@apollo/client';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { AppNames } from '@openx/components-core/lib/PageNotFound/PageNotFound.tsx';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { CreateBidderCreativeDocument } from '../../graphql/mutation/creatives/CreateBidderCreative.generated.tsx';
import { useLocalErrorHandling } from '../graphql/useLocalErrorHandling.ts';
import { CreativeFormFields } from './CreativeForm/CreativeFormFields.tsx';
import { getCreativeCreateInitialState, transformFormDataToMutationInput } from './CreativeForm/utils.tsx';
import type { CreativeFormState } from './CreativeForm/validation/validationSchema.ts';
import { useFetchRequiredOptions } from './hooks/useFetchRequiredOptions.ts';

export const CreateCreativePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { advertiserOptions, iabCategoryOptions, isLoading } = useFetchRequiredOptions({ includeIabCategories: true });
  const initialValues = getCreativeCreateInitialState(advertiserOptions);
  const [createBidderCreative] = useMutation(CreateBidderCreativeDocument);

  useLocalErrorHandling(CreateBidderCreativeDocument, () => true);

  const handleCreate = async (data: CreativeFormState): Promise<void> => {
    const input = transformFormDataToMutationInput(data);
    const result = await createBidderCreative({
      variables: { input },
      update: cache => {
        cache.evict({ fieldName: 'bidderCreatives' });
        cache.gc();
      },
    });

    if (result.data?.createBidderCreative) {
      enqueueSnackbar(t('Creative created successfully'), { variant: 'success' });
      navigate(`/aqua/creatives/${result.data.createBidderCreative.id}`);
    }
  };

  if (isLoading) {
    return (
      <PageHeader
        title={t('Create New Creative')}
        titlePrefix={AppNames.OpenXSelect}
        goBack={() => navigate('/aqua/creatives')}
      >
        <Loader />
      </PageHeader>
    );
  }

  return (
    <PageHeader
      title={t('Create New Creative')}
      titlePrefix={AppNames.OpenXSelect}
      goBack={() => navigate('/aqua/creatives')}
    >
      <CreativeFormFields
        advertiserOptions={advertiserOptions}
        iabCategoryOptions={iabCategoryOptions}
        initialValues={initialValues}
        onSubmit={handleCreate}
      />
    </PageHeader>
  );
};
